import { useState } from 'react';
import OrnamentSeparator from '../components/OrnamentSeparator';
import { useCountdown } from '../hooks/useCountdown';

interface ConsiglioScreenProps {
  endsAt: number;
  serverTime: number;
  initialHasSubmitted?: boolean;
}

export default function ConsiglioScreen({ endsAt, serverTime, initialHasSubmitted }: ConsiglioScreenProps) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(initialHasSubmitted || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remainingSeconds = useCountdown(endsAt, serverTime);

  const sessionId = localStorage.getItem('sessionId');
  const audienceId = localStorage.getItem('audienceId');
  const nickname = localStorage.getItem('nickname') || 'Anonimo';
  const teamColor = localStorage.getItem('teamColor') || 'BLUE';

  const handleSubmit = async () => {
    if (!text.trim() || !sessionId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/consiglio/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          text: text.trim(),
          nickname,
          team: teamColor,
          audienceId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore invio');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Errore invio consiglio');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <p className="text-5xl mb-6 font-heading">&#10003;</p>
          <h2 className="text-3xl font-heading font-bold text-ink mb-2">Grazie!</h2>
          <p className="text-ink-light font-body">Il tuo consiglio e' stato inviato</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="w-full max-w-md">
        {/* Countdown */}
        <div className="text-center mb-4">
          <div className={`text-5xl font-bold ${remainingSeconds <= 5 ? 'text-team-red animate-pulse' : 'text-ink'}`}>
            {remainingSeconds}
          </div>
          <p className="text-ink-light mt-1 text-sm font-body">secondi rimasti</p>
        </div>

        <h2 className="text-3xl font-heading font-bold text-ink text-center mb-2">
          Cosa consigli?
        </h2>

        <OrnamentSeparator className="mb-6" />

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 200))}
          placeholder="Scrivi il tuo consiglio..."
          maxLength={200}
          rows={4}
          className="w-full bg-parchment-50 text-ink px-4 py-3 rounded-xl border-2 border-parchment-300 focus:outline-none focus:ring-2 focus:ring-parchment-400 resize-none text-lg font-body"
        />

        <div className="flex justify-between items-center mt-2 mb-4">
          <span className="text-ink-light text-base font-body">
            {text.length}/200
          </span>
          {error && (
            <span className="text-team-red text-base font-body">{error}</span>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!text.trim() || isSubmitting || remainingSeconds <= 0}
          className="w-full btn-fantasy text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Invio...' : 'Invia'}
        </button>
      </div>
    </div>
  );
}
